import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { mapRefDataPickList } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';

export const BoomTypeElevatingWorkPlatformDesignDetails = React.forwardRef((props, ref) => {
  const {
    platformType: savedPlatformType = '',
    otherPlatformType: otherSavedPlatformType = '',
    maxRatedCapacity: savedMaxRatedCapacity = '',
    designUse: savedDesignUse = '',
    maxChassisInclination: savedMaxChasisInclination = '',
    maxRatedWindVelocity: savedMaxRatedWindVelocity = '',
    insulationType: savedInsulationType = '',
    insulationRating: savedInsulationRating = '',
    maxPlatformHeight: savedMaxPlatformType = '',
    maxBoomLength: savedMaxBoomLength = '',
  } = props.value || {};

  const [platformType, setPlatformType] = useState(savedPlatformType);
  const [otherPlatformType, setOtherPlatformType] = useState(otherSavedPlatformType);
  const [maxRatedCapacity, setMaxRatedCapacity] = useState(savedMaxRatedCapacity);
  const [designUse, setDesignUse] = useState(savedDesignUse);
  const [maxChassisInclination, setMaxChassisInclination] = useState(savedMaxChasisInclination);
  const [maxRatedWindVelocity, setMaxRatedWindVelocity] = useState(savedMaxRatedWindVelocity);
  const [insulationType, setInsulationType] = useState(savedInsulationType);
  const [insulationRating, setInsulationRating] = useState(savedInsulationRating);
  const [maxPlatformHeight, setMaxPlatformHeight] = useState(savedMaxPlatformType);
  const [maxBoomLength, setMaxBoomLength] = useState(savedMaxBoomLength);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.boomTypeElevatingWorkPlatform;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.boomTypeElevatingWorkPlatform;

  const designUseOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.designUsesOptions]);
  const insulationTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.insulationTypeOptions]);

  const validators = [
    {
      fieldName: 'maxRatedCapacity',
      fieldValue: maxRatedCapacity,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'designUse',
      fieldValue: designUse,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'maxChassisInclination',
      fieldValue: maxChassisInclination,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxRatedWindVelocity',
      fieldValue: maxRatedWindVelocity,
      isValid: (v) => isNumeric(v) || designUse === 'Indoor only',
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'insulationType',
      fieldValue: insulationType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'insulationRating',
      fieldValue: insulationRating,
      isValid: (v) => isNumeric(v) || insulationType === 'Non-insulated',
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxPlatformHeight',
      fieldValue: maxPlatformHeight,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxBoomLength',
      fieldValue: maxBoomLength,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isValid);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      maxRatedCapacity,
      designUse,
      maxChassisInclination,
      maxRatedWindVelocity,
      insulationType,
      insulationRating,
      maxPlatformHeight,
      maxBoomLength,
    };

    if (designUse !== 'Indoor & Outdoor') delete details.maxRatedWindVelocity;

    if (insulationType !== 'Insulated') delete details.insulationRating;

    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    maxRatedCapacity,
    designUse,
    maxChassisInclination,
    maxRatedWindVelocity,
    insulationType,
    insulationRating,
    maxPlatformHeight,
    maxBoomLength,
  ]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <>
        <FormGroup
          id="maxRatedCapacity"
          label={PAGE_CONSTANTS.maxRatedCapacity.label}
          isRequired
          errorMessage={errors?.maxRatedCapacity?.errorMessage}
          hasError={showErrors && errors?.maxRatedCapacity?.invalid}
          className={styles.inputWithSuffix}
          margin={{ top: 'xl' }}
        >
          <Input
            suffix={PAGE_CONSTANTS.maxRatedCapacity.suffix}
            onChange={(e) => setMaxRatedCapacity(e.target.value)}
            value={maxRatedCapacity}
            inputWidth="lg"
          />
        </FormGroup>

        <FormRadioGroup
          id="designUse"
          name="designUse"
          legend={PAGE_CONSTANTS.designUse.label}
          vertical={true}
          options={designUseOptions}
          onChange={setDesignUse}
          value={designUse}
          errorMessage={errors?.designUse?.errorMessage}
          hasError={showErrors && errors?.designUse?.invalid}
          margin={{ top: 'md' }}
        />

        <FormGroup
          id="maxChassisInclination"
          label={PAGE_CONSTANTS.maxChassisInclination.label}
          isRequired
          errorMessage={errors?.maxChassisInclination?.errorMessage}
          hasError={showErrors && errors?.maxChassisInclination?.invalid}
          className={styles.inputWithSuffix}
          margin={{ top: 'xl' }}
        >
          <Input
            suffix={PAGE_CONSTANTS.maxChassisInclination.suffix}
            onChange={(e) => setMaxChassisInclination(e.target.value)}
            value={maxChassisInclination}
            inputWidth="md"
          />
        </FormGroup>

        {designUse === 'Indoor & Outdoor' && (
          <FormGroup
            id="maxRatedWindVelocity"
            label={PAGE_CONSTANTS.maxRatedWindVelocity.label}
            isRequired
            errorMessage={errors?.maxRatedWindVelocity?.errorMessage}
            hasError={showErrors && errors?.maxRatedWindVelocity?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxRatedWindVelocity.suffix}
              onChange={(e) => setMaxRatedWindVelocity(e.target.value)}
              value={maxRatedWindVelocity}
              inputWidth="md"
            />
          </FormGroup>
        )}

        <FormRadioGroup
          id="insulationType"
          name="insulationType"
          legend={PAGE_CONSTANTS.insulationType.label}
          vertical={true}
          options={insulationTypeOptions}
          onChange={setInsulationType}
          value={insulationType}
          errorMessage={errors?.insulationType?.errorMessage}
          hasError={showErrors && errors?.insulationType?.invalid}
          margin={{ top: 'md' }}
        />

        {insulationType === 'Insulated' && (
          <FormGroup
            id="insulationRating"
            label={PAGE_CONSTANTS.insulationRating.label}
            isRequired
            errorMessage={errors?.insulationRating?.errorMessage}
            hasError={showErrors && errors?.insulationRating?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'xl' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.insulationRating.suffix}
              onChange={(e) => setInsulationRating(e.target.value)}
              value={insulationRating}
              inputWidth="md"
            />
          </FormGroup>
        )}

        <FormGroup
          id="maxPlatformHeight"
          label={PAGE_CONSTANTS.maxPlatformHeight.label}
          helpMessage={PAGE_CONSTANTS.maxPlatformHeightHelpText}
          isRequired
          errorMessage={errors?.maxPlatformHeight?.errorMessage}
          hasError={showErrors && errors?.maxPlatformHeight?.invalid}
          className={styles.inputWithSuffix}
          margin={{ top: insulationType === 'Insulated' ? 'md' : 'xl' }}
        >
          <Input
            suffix={PAGE_CONSTANTS.maxPlatformHeight.suffix}
            onChange={(e) => setMaxPlatformHeight(e.target.value)}
            value={maxPlatformHeight}
            inputWidth="md"
          />
        </FormGroup>

        <FormGroup
          id="maxBoomLength"
          label={PAGE_CONSTANTS.maxBoomLength.label}
          helpMessage={PAGE_CONSTANTS.maxBoomLengthHelpText}
          isRequired
          errorMessage={errors?.maxBoomLength?.errorMessage}
          hasError={showErrors && errors?.maxBoomLength?.invalid}
          className={styles.inputWithSuffix}
          margin={{ top: 'md' }}
        >
          <Input
            suffix={PAGE_CONSTANTS.maxBoomLength.suffix}
            onChange={(e) => setMaxBoomLength(e.target.value)}
            value={maxBoomLength}
            inputWidth="md"
          />
        </FormGroup>
      </>
    </>
  );
});

BoomTypeElevatingWorkPlatformDesignDetails.displayName = 'BoomTypeElevatingWorkPlatformDesignDetails';

BoomTypeElevatingWorkPlatformDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
